import React from 'react'
import MainLayout from '../components/MainLayout'
import DashboardCard from '../components/DashboardCard'
import RecentOrdersTable from '../components/RecentOrdersTable'
import Contributions from '../charts/Contributions'
import { useQuery } from '@apollo/client'
import { GET_DASHBOARD } from '../apollo/Queries'
import { Hotel, HotelIcon, ListCheckIcon, ListCollapseIcon, ListX, UsersRound, Sparkles } from 'lucide-react'

export default function DashboardPage() {
  const { data, loading } = useQuery(GET_DASHBOARD)
  const dashboard = data?.dashboardStats?.data;

  return (
    <MainLayout page="Dashboard">
      <div className="space-y-10 animate-fade-in-up">
        {/* Welcome Banner */}
        <section className="relative overflow-hidden bg-gradient-to-br from-premium-teal to-premium-dark rounded-[2.5rem] p-10 text-white shadow-2xl">
          <div className="absolute top-0 right-0 w-96 h-96 bg-premium-gold/10 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/2"></div>
          <div className="relative z-10 flex flex-col md:flex-row justify-between items-center gap-8">
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <Sparkles className="text-premium-gold animate-glow" size={24} />
                <span className="text-xs font-black uppercase tracking-[0.4em] text-premium-gold/80">Business Overview</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-black tracking-tighter">Restaurant Dashboard</h2>
              <p className="text-teal-100/60 font-light max-w-md">Manage your restaurants and monitor order performance in real-time.</p>
            </div>
            <div className="bg-white/5 backdrop-blur-xl border border-white/10 p-6 rounded-[2rem] flex items-center gap-6 shadow-inner">
              <div className="text-center">
                <p className="text-[10px] text-teal-700 font-black uppercase tracking-widest text-premium-gold mb-1">Status</p>
                <span className="px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-[10px] font-bold uppercase border border-green-500/30">Synchronized</span>
              </div>
              <div className="w-[1px] h-10 bg-white/10"></div>
              <div className="text-center">
                <p className="text-[10px] text-teal-700 font-black uppercase tracking-widest text-premium-gold mb-1">Protocol</p>
                <span className="text-sm text-teal-700 font-black px-2">ODAZ-7G</span>
              </div>
            </div>
          </div>
        </section>

        {loading ? (
          <div className="flex flex-col justify-center items-center h-64 gap-6">
            <div className="relative">
              <div className="w-20 h-20 rounded-2xl border-4 border-premium-gold/20 border-t-premium-gold animate-spin"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-10 h-10 rounded-xl bg-premium-teal/10 animate-pulse"></div>
              </div>
            </div>
            <p className="text-xs font-black text-premium-gold uppercase tracking-[0.5em] animate-pulse">Gathering Essence...</p>
          </div>
        ) : dashboard ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              <DashboardCard
                available={dashboard.totalOrders}
                percent="Orders"
                title="Available Orders"
                icon={<ListCollapseIcon />}
              />
              <DashboardCard
                available={dashboard.totalPendingOrders}
                percent="Pending"
                title="Pending Orders"
                icon={<ListX />}
              />
              <DashboardCard
                available={dashboard.totalAcceptedOrders}
                percent="Active"
                title="Accepted Orders"
                icon={<ListCheckIcon />}
              />
              <DashboardCard
                available={dashboard.totalActiveRestaurants}
                percent="Nodes"
                title="Active Restaurants"
                icon={<Hotel />}
              />
              <DashboardCard
                available={dashboard.totalProducts}
                percent="Items"
                title="Products"
                icon={<HotelIcon />}
              />
              <DashboardCard
                available={dashboard.totalCustomers}
                percent="Elite"
                title="Registered Customers"
                icon={<UsersRound />}
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
              <div className="bg-white rounded-[2.5rem] shadow-[0_20px_60px_rgba(0,0,0,0.03)] border border-gray-100 p-10 overflow-hidden relative group">
                <div className="absolute top-0 right-0 w-32 h-32 bg-premium-teal/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 group-hover:bg-premium-gold/5 transition-colors"></div>
                <div className="flex items-center justify-between mb-8">
                  <div>
                    <h3 className="text-2xl font-black text-premium-teal tracking-tighter">Flow Analytics</h3>
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mt-1">Orders Pulse</p>
                  </div>
                  <div className="w-10 h-10 rounded-xl bg-premium-teal/5 flex items-center justify-center text-premium-teal">
                    <Sparkles size={18} />
                  </div>
                </div>
                <Contributions total={dashboard.totalOrders} pending={dashboard.totalPendingOrders} accepted={dashboard.totalAcceptedOrders} />
              </div>

              <div className="bg-white rounded-[2.5rem] shadow-[0_20px_60px_rgba(0,0,0,0.03)] border border-gray-100 p-10 overflow-hidden relative group">
                <div className="absolute top-0 right-0 w-32 h-32 bg-premium-teal/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 group-hover:bg-premium-gold/5 transition-colors"></div>
                <div className="flex items-center justify-between mb-8">
                  <div>
                    <h3 className="text-2xl font-black text-premium-teal tracking-tighter">Recent Orders</h3>
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mt-1">Live Feed</p>
                  </div>
                  <button className="text-[10px] font-black text-premium-gold uppercase tracking-[0.2em] hover:text-premium-teal transition-colors">View All</button>
                </div>
                <RecentOrdersTable limit={5} />
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-20 bg-white rounded-[2.5rem] border border-dashed border-gray-200">
            <p className="text-gray-400 font-light italic">The essence has not yet materialized.</p>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
